/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { error as logError } from '@/data/utils/loggerUtil';
import { transactionsGeonode } from 'integration/generated/transactions';
import { Geonode } from 'integration/generated/transactions/Geonode';
import { GetServerSidePropsContext } from 'next';
import useSWR from 'swr';

type PropsGeonodeFindTopGeoNodes = Parameters<Geonode['geonodeFindTopGeoNodes']>;
type QueryGeonodeFindTopGeoNodes = PropsGeonodeFindTopGeoNodes[1];

type PropsGeonodeFindGeoByParentGeoId = Parameters<Geonode['geonodeFindGeoByParentGeoId']>;

function normalizarString(str: String) {
	return str
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
}

const homologatePhysical = (data: any, { response: dataStates }: any) => {
	if (!data || !dataStates) {
		return [];
	}

	const estados = dataStates.map((e: any) => normalizarString(e.toLowerCase()));
	data.GeoNode = data.GeoNode.filter((elm: any) =>
		estados.includes(normalizarString(elm.Description[0].shortDescription.toLowerCase()))
	);
	return data;
};

const fetcherGeonodeFindTopGeoNodes =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		query,
		params = {},
	}: {
		storeId: PropsGeonodeFindTopGeoNodes[0];
		query: QueryGeonodeFindTopGeoNodes;
		params: PropsGeonodeFindTopGeoNodes[2];
	}): Promise<ReturnType<Geonode['geonodeFindTopGeoNodes']> | void> => {
		try {
			const data = await transactionsGeonode(pub).geonodeFindTopGeoNodes(
				storeId,
				query as PropsGeonodeFindTopGeoNodes[1],
				params
			);
			return data;
		} catch (error) {
			logError(context?.req, 'geonodeFindTopGeoNodes: fetcher: error: %o', error);
		}
	};

const fetcherGeonodeFindGeoByParentGeoId =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		parentgeoid,
		query,
		params = {},
	}: {
		storeId: PropsGeonodeFindGeoByParentGeoId[0];
		parentgeoid: PropsGeonodeFindGeoByParentGeoId[1];
		query: QueryGeonodeFindTopGeoNodes;
		params: PropsGeonodeFindGeoByParentGeoId[3];
	}): Promise<ReturnType<Geonode['geonodeFindGeoByParentGeoId']> | void> => {
		try {
			const data = await transactionsGeonode(pub).geonodeFindGeoByParentGeoId(
				storeId,
				parentgeoid,
				query as PropsGeonodeFindGeoByParentGeoId[2],
				params
			);
			const dataStates = await transactionsGeonode(pub).locationFindPhysicalStore(
				storeId,
				parentgeoid,
				query as PropsGeonodeFindGeoByParentGeoId[2],
				params
			);

			const homologateStates = homologatePhysical(data, dataStates);
			return homologateStates;
		} catch (error) {
			logError(context?.req, 'geonodeFindGeoByParentGeoId: fetcher: error: %o', error);
		}
	};

export const useGeonode = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	// const { query } = router;
	const params = useExtraRequestParameters();

	const { storeId } = getClientSideCommon(settings, router);

	const { data } = useSWR(
		storeId
			? [
					{
						storeId,
						query: {
							responseFormat: 'json',
							siteLevelSearch: 'false',
						} as const,
						params: {},
					},
			  ]
			: null,
		async ([props]) => fetcherGeonodeFindTopGeoNodes(true)({ ...props, params }),
		{ use: [laggyMiddleWare] }
	);

	const { data: dataStates } = useSWR(
		storeId && data
			? [
					{
						storeId,
						parentgeoid: (data as { uniqueID: string })?.uniqueID || '',
						query: {
							responseFormat: 'json',
							siteLevelSearch: 'false',
						} as const,
						params: {},
					},
			  ]
			: null,
		async ([props]) => fetcherGeonodeFindGeoByParentGeoId(true)({ ...props, params }),
		{ use: [laggyMiddleWare] }
	);

	return {
		data,
		dataStates,
	};
};
